import type { ExtendedMedicationRequest } from '@seen/core/models/medicationRequest'
import { Badge, cn } from '@seen/ui'
import type { ComponentProps, JSX } from 'react'

type MedicationState = ReturnType<
  typeof ExtendedMedicationRequest.classifyMedicationState
>

// Every medication badge (state, Rx/OTC, issue) shares one geometry so badges
// line up wherever they sit side by side, regardless of each badge's variant.
export const MEDICATION_BADGE_CLASS =
  'h-5 shrink-0 px-2 py-0 text-[11px] leading-none'

const STATE_VARIANT: Record<
  MedicationState['category'],
  'default' | 'secondary' | 'outline'
> = {
  'active-current': 'default',
  'active-likely-finished': 'default',
  'active-expired': 'outline',
  'active-outdated': 'outline',
  stopped: 'secondary',
  cancelled: 'secondary',
  completed: 'secondary',
  unknown: 'outline',
}

const STATE_COLOR: Record<
  MedicationState['category'],
  'green' | 'gray' | 'red' | 'orange' | undefined
> = {
  'active-current': 'green',
  'active-likely-finished': 'green',
  'active-expired': 'red',
  'active-outdated': 'orange',
  stopped: 'gray',
  cancelled: 'gray',
  completed: 'gray',
  unknown: undefined,
}

export function MedicationStateBadge({
  state,
  emphasizeAction = false,
  className,
  ...props
}: Omit<ComponentProps<typeof Badge>, 'variant' | 'color'> & {
  state: MedicationState
  /* Bold a filled badge whose state requires action */
  emphasizeAction?: boolean
}): JSX.Element {
  const variant = STATE_VARIANT[state.category]
  return (
    <Badge
      {...props}
      variant={variant}
      color={STATE_COLOR[state.category]}
      data-medication-state={state.category}
      className={cn(
        MEDICATION_BADGE_CLASS,
        emphasizeAction &&
          variant !== 'outline' &&
          state.requiresAction &&
          'font-semibold',
        className
      )}
    >
      {state.category.startsWith('active-') ? 'Active' : state.label}
    </Badge>
  )
}

export function MedicationTypeBadge({
  type,
}: {
  type: string | undefined
}): JSX.Element | null {
  if (!type) {
    return null
  }
  return (
    <Badge variant="outline" className={MEDICATION_BADGE_CLASS}>
      {type.includes('prescription') ? 'Rx' : 'OTC'}
    </Badge>
  )
}
